import { addDays, format, isBefore, isToday, parseISO } from "date-fns";
import React, { useEffect, useRef, useCallback, type PropsWithChildren, type ReactNode } from "react";
import ActionLogger from "action-logger";
import {
  analytics,
  dateFnsFormats,
  fallbackZip,
  flagsAndExperimentNames,
  gridTypes,
  RENTAL_INTENT,
  sizeFilterLabels,
} from "rtr-constants";
import deliveryCutoffActions from "actions/delivery-cutoff-actions";
import { getSizeLabel, formattedDeliveryDate } from "helpers/dfc-ux-helper";
import GridSuperBar from "components/source/new_taxonomy/grid-superbar";
import GridTopFiltersAddressModal from "components/source/new_taxonomy/grid-top-filters-address-modal";
import GridAvailabilityModal from "components/source/new_taxonomy/grid-availability-modal";
import GridTopFiltersSizeModal from "components/source/new_taxonomy/grid-top-filters-size-modal";
import GridTopFiltersZipModal from "components/source/new_taxonomy/grid-top-filters-zip-modal";
import { inMaternityExperience } from "helpers/maternity-helper";
import MembershipHelpers, { isProspectWithSubscriptionIntent } from "helpers/membership-helpers";
import MembershipRentalBeginActions from "actions/membership-rental-begin-actions";
import productsActions from "actions/products-actions";
import { loadAddressBook } from "actions/shipping-step-actions";
import { isAdmin } from "components/source/hoc/with-user-data";
import PinIcon from "images/sprites/reb/svg/superbar-pin.svg";
import CalIcon from "images/sprites/reb/svg/superbar-cal.svg";
import FilterIcon from "images/sprites/reb/svg/filter-icon.svg";
import RtrParcelIcon from "images/sprites/reb/svg/rtr-parcel.svg";
import { isFilteredByShippingAddress, isFilteredByShippingAddressNext } from "helpers/grid-top-filters-helpers";
import {
  closeModal,
  openGridAvailabilityModal,
  openShippingFilterModal,
  openSizeFilterModal,
  openZipFilterModal,
  toggleGridFilterModal,
} from "actions/grid-modal-actions";
import { isStorefrontNext } from "helpers/environment-helpers";
import HeapHelpers from "helpers/heap-helpers";
import GridAvailabilityToggle from "./grid-availability-toggle";
import RtrPill from "components/design-system/rtr-pill";
import classNames from "classnames";
import CalendarDrawerModal from "../reservations/calendar-drawer/calendar-drawer-modal";
import { MODAL_NAMES } from "constants/modal-names";
import type { Address } from "@rtr/godmother";
import type { WorkingFilters } from "components/types/working-filters";
import { useAppDispatch, useAppSelector } from "../../../../../util/store-types";
import { isViewingAvailableOnly } from "helpers/grid-migration-helpers";
import type { WithRequiredFields } from "components/types/type-utils";
import { useAvailabilityFilters } from "hooks/use-availability-filters";
import { useWorkingFilter } from "hooks/use-working-filter";
import { useOnLoadFlagOrExperiment } from "../hoc/with-flags-and-experiments";
import { useDisplayedFilters } from "hooks/use-displayed-filters";

type GridTopFiltersComponentProps = PropsWithChildren<{
  currentIntent: keyof typeof RENTAL_INTENT;
  gridType: string;
  hideZipFilter: boolean;
  list: { id: string; name: string }[];
  resultsCount: number;
  scrollToTopFilters: () => void;
  submitFilters: () => void;
  renderQuickFilters?: () => ReactNode;
}>;

const GridTopFiltersComponent = ({
  currentIntent,
  gridType,
  hideZipFilter,
  list,
  resultsCount,
  renderQuickFilters = () => null,
  scrollToTopFilters = () => {},
  submitFilters,
}: GridTopFiltersComponentProps) => {
  const browser = useAppSelector(state => state.browser);
  const dateOptions = useAppSelector(state => state.dateOptions);
  const deliveryCutoffs = useAppSelector(state => state.deliveryCutoffs);
  const displayedModal = useAppSelector(state => state.displayedModal);
  const gridView = useAppSelector(state => state.gridView) || {};
  const membershipRentalBeginDate = useAppSelector(state => state.membershipRentalBeginDate);
  const membershipState = useAppSelector(state => state.membershipState);
  const userData = useAppSelector(state => state.userData || {});
  const workingFilters = useAppSelector(state => state.workingFilters || {});
  const isAvailabilityApplicable = useAppSelector(state => state.gridMetaData?.isAvailabilityApplicable);
  const isSwapsGrid = useAppSelector(state => state.isSwapsGrid);
  const { displayedFilterCount } = useDisplayedFilters();

  const authDataLoaded = useAppSelector(state => state.userData?.authDataHasLoaded);
  const isIdentifiedUser = useAppSelector(state => Boolean(state.userData?.userProfile));
  const addresses = useAppSelector(state => state.addresses);
  const addressesLoading = useAppSelector(state => state.isAddressBookLoading);
  const isAdminUser = useAppSelector(state => isAdmin(state.userData));
  const [reserveAsFilterFlag] = useOnLoadFlagOrExperiment(flagsAndExperimentNames.RESERVE_FILTER);

  const { setOrToggleMembershipAvailability, setReserveAvailability } = useAvailabilityFilters(submitFilters);
  const [workingFiltersZipCode, setWorkingFiltersZipCode] = useWorkingFilter("zip_code", submitFilters);
  const [workingFiltersSizes, setWorkingFiltersSizes] = useWorkingFilter("canonicalSizes", submitFilters);

  const dispatch = useAppDispatch();

  const isShortlistGrid = typeof list !== "undefined";

  const gridTopFilters = useRef(null);

  const loggingObjectType = analytics.OBJECT_TYPE.GRID;

  const logDefaultLens = () => {
    const lens = MembershipHelpers.getUserDataLens(userData);

    ActionLogger.logAction({
      object_type: loggingObjectType,
      action: "loads",
      default_lens: lens,
    });
  };

  const logZipSaved = (zipCode: string) => {
    const lens = MembershipHelpers.getUserDataLens(userData);

    const heapEventData = {
      lens: lens,
      zipCode: zipCode,
      isProspectWithSubscriptionIntent: isProspectWithSubscriptionIntent(membershipState, currentIntent),
    };

    HeapHelpers.fireHeapEvent("zip_code_click_confirm", heapEventData);

    ActionLogger.logAction({
      object_type: loggingObjectType,
      action: "zip_code_click_confirm",
      zip: zipCode,
      lens: lens,
    });
  };

  const chosenDateNotAvailableAnymore = useCallback((): boolean => {
    const { date: selectedDate } = workingFilters;
    const deliveryCutoff = deliveryCutoffs?.[0];
    const possibleDeliveryDate = deliveryCutoff?.deliveryDate;
    const { blackoutDays } = dateOptions || {};

    if (selectedDate && blackoutDays?.indexOf(selectedDate) >= 0) {
      return true;
    }

    if (!selectedDate || !deliveryCutoffs?.length || !possibleDeliveryDate) {
      return false;
    }

    return isBefore(parseISO(selectedDate), parseISO(possibleDeliveryDate));
  }, [workingFilters, deliveryCutoffs, dateOptions]);

  const getAvailabilityLabelIcon = () => {
    const { isClassicLens } = userData;

    return isClassicLens ? <CalIcon /> : <RtrParcelIcon className="mb:w-4 mb:h-4 w-5 h-5" />;
  };

  const getReserveAvailabilityCopy = (): string => {
    const { date } = workingFilters;
    if (!date) {
      return "Choose dates";
    }

    const dateFormat = dateFnsFormats.M_DD;
    const formattedRentalBeginDate = format(parseISO(date), dateFormat);
    const defaultRentalDuration = 4;
    const duration = workingFilters?.duration ? parseInt(workingFilters.duration) : defaultRentalDuration;
    const formattedRentalEndDate = format(addDays(parseISO(date), duration - 1), dateFormat);

    return `Reserve ${formattedRentalBeginDate} - ${formattedRentalEndDate}`;
  };

  const getSubscriptionAvailabilityCopy = (): string => {
    const availableOnly = isViewingAvailableOnly(workingFilters, membershipState);

    if (!availableOnly) {
      return "Rent with Membership";
    } else {
      //These should match, but for active subs prefer membershipState as source of truth
      const deliveryDate = MembershipHelpers.getRentBeginDate(membershipState) ?? membershipRentalBeginDate;
      let copy = "Arrives by";
      if (deliveryDate && isToday(parseISO(deliveryDate))) {
        copy = "Arrives";
      }

      return `${copy} ${formattedDeliveryDate(deliveryDate) ?? ""}`;
    }
  };

  const getAvailabilityLabelCopy = () => {
    const { isClassicLens } = userData;

    return isClassicLens ? getReserveAvailabilityCopy() : getSubscriptionAvailabilityCopy();
  };

  const getLocationLabel = (): string => {
    if (!isFilteredByShippingAddress(userData, gridView)) {
      return workingFiltersZipCode ?? fallbackZip;
    }

    // membershipBagAddressZip is not accurate naming for the const value
    // see comment in MembershipHelpers.getShipmentZip
    const membershipBagAddressZip = MembershipHelpers.getShipmentZip(membershipState);
    const copy = membershipBagAddressZip;
    return copy;
  };

  const getSizesLabel = () => {
    const selectedSizes = workingFiltersSizes || [];
    return getSizeLabel(selectedSizes, inMaternityExperience(userData));
  };

  const initialiseAddressData = useCallback(() => {
    if (isIdentifiedUser && !Array.isArray(addresses) && !addressesLoading) {
      dispatch(loadAddressBook());
    }
  }, [addresses, dispatch, addressesLoading, isIdentifiedUser]);

  useEffect(() => {
    logDefaultLens();
  }, []);

  useEffect(() => {
    const zipCode = workingFiltersZipCode ?? fallbackZip;

    if (isStorefrontNext()) {
      dispatch(deliveryCutoffActions.fetchBlackoutDays(zipCode));
      dispatch(deliveryCutoffActions.fetchDeliveryCutoffs(zipCode));
    }

    if (isStorefrontNext() || isShortlistGrid) {
      dispatch(MembershipRentalBeginActions.fetchMembershipRentalBegin(zipCode));
    }
  }, [workingFiltersZipCode, isShortlistGrid, dispatch]);

  useEffect(initialiseAddressData, [authDataLoaded, initialiseAddressData]);

  useEffect(
    function handleAvailableDatesChange() {
      // Allows CX to override delivery cutoffs
      if (isAdminUser) {
        return;
      } else if (chosenDateNotAvailableAnymore()) {
        setOrToggleMembershipAvailability();
      }
    },
    [isAdminUser, chosenDateNotAvailableAnymore]
  );

  const onClickLocationGridButton = () => {
    if (isStorefrontNext()) {
      if (userData?.userProfile?.addresses) {
        return isFilteredByShippingAddressNext(userData)
          ? dispatch(openShippingFilterModal())
          : dispatch(openZipFilterModal());
      } else {
        return dispatch(openZipFilterModal());
      }
    } else {
      return isFilteredByShippingAddress(userData, gridView)
        ? dispatch(openShippingFilterModal())
        : dispatch(openZipFilterModal());
    }
  };

  const handleZipCodeChange = (updatedFilterObject: WithRequiredFields<WorkingFilters, "zip_code">) => {
    setWorkingFiltersZipCode(updatedFilterObject.zip_code);
  };

  const onClickZipSubmit = (zipCodeFilter: WithRequiredFields<WorkingFilters, "zip_code">) => {
    const zipCode = zipCodeFilter.zip_code;
    if (isStorefrontNext()) {
      dispatch(productsActions.refreshMembershipDepotIdCookie(zipCode, handleZipCodeChange));
    } else {
      handleZipCodeChange(zipCodeFilter);
    }

    if (displayedModal !== MODAL_NAMES.CALENDAR_DRAWER) {
      dispatch(closeModal());
    }

    logZipSaved(zipCode);
  };

  const onChangeAddress = (address: Address) => {
    const { postalCode } = address;
    const zipCodeFilter = { zip_code: postalCode };
    const isSubscriptionMember = MembershipHelpers.isSubscriptionMember(userData);
    if (isStorefrontNext()) {
      dispatch(productsActions.refreshMembershipDepotIdCookie(postalCode, handleZipCodeChange));
    } else {
      handleZipCodeChange(zipCodeFilter);
    }

    if (!isSubscriptionMember) {
      dispatch(MembershipRentalBeginActions.fetchMembershipRentalBegin(postalCode));
    }
  };

  const onClickSizeSubmit = (sizeFilter: WithRequiredFields<WorkingFilters, "canonicalSizes">) => {
    const { canonicalSizes } = sizeFilter;
    setWorkingFiltersSizes(canonicalSizes);
    dispatch(closeModal());

    const heapEventData = {
      sizes: canonicalSizes.join(","),
      isProspectWithSubscriptionIntent: isProspectWithSubscriptionIntent(membershipState, currentIntent),
    };

    HeapHelpers.fireHeapEvent("size_select", heapEventData);

    ActionLogger.logAction({
      object_type: loggingObjectType,
      action: "size_select",
      itemSizes: canonicalSizes.join(","),
    });
  };

  const onClickMaternityText = () => {
    if (inMaternityExperience(userData)) {
      return;
    }

    ActionLogger.logAction({
      object_type: loggingObjectType,
      action: "enter_maternity_flow",
    });
  };

  const onClickOptOut = () => {
    ActionLogger.logAction({
      object_type: loggingObjectType,
      action: "opt_out_maternity_flow",
    });
  };

  const onClickSaveDueDate = (dueDate: string) => {
    ActionLogger.logAction({
      object_type: loggingObjectType,
      action: "submit_due_date",
      due_date: dueDate,
    });
  };

  const onClickDateSubmit = (date: string | null, duration?: "4" | "8") => {
    ActionLogger.logAction({
      object_type: loggingObjectType,
      action: "rezo_select",
      rentBegin: date,
      zip: workingFiltersZipCode ?? "",
      itemSizes: (workingFiltersSizes || []).join(","),
    });

    dispatch(closeModal());

    //If the top filters are not currently in view, scroll them into view so users see the filter change
    scrollToTopFilters();

    setReserveAvailability(date, duration);
  };

  const renderFilterAndSort = () => {
    if (isShortlistGrid) {
      return null;
    }

    //Only show a count if filters have been applied
    const filtersAppliedText = displayedFilterCount > 0 ? `Sort + Filter (${displayedFilterCount})` : "Sort + Filter";

    const icon = <FilterIcon />;
    const containerClassNames = classNames("flex justify-end w-100% align-center items-center gap-2 md:gap-0", {
      "basis-1/2 md:basis-1/3": !reserveAsFilterFlag,
      "basis-[200px]": reserveAsFilterFlag,
    });
    const pillClassNames = classNames("whitespace-nowrap", {
      "shadow-md": reserveAsFilterFlag,
      "bg-offWhite": !reserveAsFilterFlag,
    });

    return (
      <div id="attribute-filter-button" className={containerClassNames}>
        <RtrPill
          className={pillClassNames}
          text={filtersAppliedText}
          onClick={() => dispatch(toggleGridFilterModal(true))}
          icon={icon}
          dataTestId="filter-pill"
        />
      </div>
    );
  };

  const getSizeHedText = () => {
    const titleText = {
      ...sizeFilterLabels,
      default: "What are your go-to sizes?",
    };

    if (inMaternityExperience(userData)) {
      return titleText.inMaternity;
    }

    return titleText.default;
  };

  const renderLocationFilter = () => {
    if (hideZipFilter || !gridView.isRentable) {
      return null;
    }

    const icon = <PinIcon />;
    const baseClass = "bg-offWhite";

    return [
      <RtrPill
        key="locationPill"
        icon={icon}
        onClick={onClickLocationGridButton}
        text={getLocationLabel()}
        className={baseClass}
        dataTestId="zip-pill"
      />,
      <GridTopFiltersZipModal
        browser={browser}
        displayedModal={displayedModal}
        onRequestClose={() => dispatch(closeModal())}
        onSubmit={onClickZipSubmit}
        workingFilters={workingFilters}
        key="gridTopFiltersZipModal"
      />,
      <GridTopFiltersAddressModal onChange={onChangeAddress} key="gridTopFiltersAddressModal" />,
    ];
  };

  const renderSizeFilter = () => {
    if (gridType === gridTypes.saleables) {
      return null;
    }

    const sizesText = getSizesLabel();
    const baseClass = "bg-offWhite";

    return (
      <>
        <RtrPill
          onClick={() => dispatch(openSizeFilterModal())}
          text={sizesText}
          key="sizeFilterButton"
          className={baseClass}
          dataTestId="size-pill"
        />
        <GridTopFiltersSizeModal
          browser={browser}
          defaultSizeHedText={getSizeHedText()}
          defaultSizeDekText={"Select a few that fit you the best."}
          displayedModal={displayedModal}
          onClickCancel={() => {}}
          onClickMaternityText={onClickMaternityText}
          onClickOptOut={onClickOptOut}
          onClickSave={onClickSaveDueDate}
          onRequestClose={() => dispatch(closeModal())}
          onSubmit={onClickSizeSubmit}
          userData={userData}
          workingFilters={workingFilters}
          key="gridTopFiltersSizeModal"
        />
      </>
    );
  };

  const renderAvailabilityFilter = () => {
    if (!gridView?.isRentable) {
      return;
    }

    const availabilityLabelContent = (
      <>
        {getAvailabilityLabelCopy()}
        <span className="reb-DownArrow"></span>
      </>
    );

    const baseClass = "bg-offWhite";

    return (
      <>
        <RtrPill
          onClick={() => dispatch(openGridAvailabilityModal())}
          text={availabilityLabelContent}
          key="availabilityFilterButton"
          className={baseClass}
          icon={getAvailabilityLabelIcon()}
          dataTestId="availability-pill"
        />
        <GridAvailabilityModal
          key="gridAvailabilityModal"
          setMembershipAvailability={setOrToggleMembershipAvailability}
        />
      </>
    );
  };

  const renderSuperBar = () => {
    if (reserveAsFilterFlag && !isShortlistGrid) {
      return null;
    }

    const sizeFilter = renderSizeFilter();
    const availabilityFilter = renderAvailabilityFilter();
    const locationFilter = renderLocationFilter();

    return (
      <GridSuperBar sizeFilter={sizeFilter} availabilityFilter={availabilityFilter} locationFilter={locationFilter} />
    );
  };

  const hideAvailabilityToggle = isSwapsGrid || !gridView?.isRentable;
  //Superbar renders in the same line as other filters on desktop, or when there is no availability toggle
  const renderSuperbarInline = !browser.isMobileViewport || hideAvailabilityToggle;

  const gridTopFiltersClasses = classNames(
    "flex",
    "justify-between",
    "w-full",
    "flex-col",
    "md:flex-row",
    "relative",
    "pl-4",
    "pr-3",
    "md:px-16",
    {
      "mt-6 md:mt-14": !resultsCount,
    }
  );

  const { isBuyNow, isClearance } = gridView;
  const noAvailabityFilterElementGrid = isBuyNow || isClearance || isSwapsGrid;
  const gridTopFiltersInnerClasses = classNames("w-full", "flex", "items-center", {
    "justify-between": !reserveAsFilterFlag || !noAvailabityFilterElementGrid,
    "justify-end": reserveAsFilterFlag && noAvailabityFilterElementGrid,
  });

  return (
    <>
      {displayedModal === MODAL_NAMES.CALENDAR_DRAWER && (
        <CalendarDrawerModal
          onClickSave={onClickDateSubmit}
          onZipSubmit={onClickZipSubmit}
          zipCode={workingFiltersZipCode}
        />
      )}
      <div className="pb-3" key="grid-top-filters">
        <div className={gridTopFiltersClasses} ref={gridTopFilters}>
          {!renderSuperbarInline && renderSuperBar()}
          <div className={gridTopFiltersInnerClasses}>
            {!hideAvailabilityToggle && (
              <GridAvailabilityToggle
                // Note: isAvailabilityApplicable === false means it's a coming soon grid or every product is coming soon item, where avaialability is always going to be false
                // whereas isAvailabilityApplicable is going to be true or undefined for other grids
                isComingSoonGrid={isAvailabilityApplicable === false}
                isShortlistGrid={isShortlistGrid}
                onSubmitFilters={submitFilters}
              />
            )}
            {renderSuperbarInline && renderSuperBar()}
            {reserveAsFilterFlag && renderQuickFilters()}
            {renderFilterAndSort()}
          </div>
        </div>
        <div>{!reserveAsFilterFlag && renderQuickFilters()}</div>
      </div>
    </>
  );
};

export default GridTopFiltersComponent;
